import { zodResolver } from "@hookform/resolvers/zod";
import { PrimaryButton } from "@mnk/frontend/components/ui/button/PrimaryButton";
import { Success } from "@mnk/frontend/components/ui/flashMessage/Success";
import { trpc } from "@mnk/frontend/lib/trpc";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const passwordResetSchema = z
  .object({
    currentPassword: z.string(),
    password: z.string(),
    passwordConfirmation: z.string(),
  })
  .refine((data) => data.password === data.passwordConfirmation, {
    message: "パスワードが一致しません",
    path: ["passwordConfirmation"],
  });

export const PasswordResetForm = () => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [trpcError, setTrpcError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<z.infer<typeof passwordResetSchema>>({
    resolver: zodResolver(passwordResetSchema),
    defaultValues: {
      currentPassword: "",
      password: "",
      passwordConfirmation: "",
    },
    mode: "onBlur",
  });

  const changePasswordMutation = trpc.changePassword.useMutation();

  const onSubmit = async (data: z.infer<typeof passwordResetSchema>) => {
    setTrpcError(null);
    setProcessing(true);
    try {
      await changePasswordMutation.mutateAsync({
        currentPassword: data.currentPassword,
        password: data.password,
      });
      setShowSuccessMessage(true);
      reset();
    } catch (error) {
      console.error(error);
      setTrpcError("パスワード変更に失敗しました");
    } finally {
      setProcessing(false);
    }
  };

  const canSubmit = useMemo(() => {
    return !processing && !errors.currentPassword && !errors.password && !errors.passwordConfirmation && isDirty;
  }, [processing, errors, isDirty]);

  return (
    <div className="w-full bg-white rounded-lg">
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <p className="text-xl font-bold">パスワード変更</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 px-4 py-8">
        <div className="flex flex-col gap-2">
          <label htmlFor="currentPassword" className="text-sm font-bold">
            現在のパスワード
          </label>
          <input
            id="currentPassword"
            type="password"
            {...register("currentPassword")}
            className="p-2 border border-gray-200 rounded-md"
          />
          {errors.currentPassword && <p className="text-sm text-red-500">{errors.currentPassword.message}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="password" className="text-sm font-bold">
            新しいパスワード
          </label>
          <input
            id="password"
            type="password"
            {...register("password")}
            className="p-2 border border-gray-200 rounded-md"
          />
          {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="passwordConfirmation" className="text-sm font-bold">
            新しいパスワード確認
          </label>
          <input
            id="passwordConfirmation"
            type="password"
            {...register("passwordConfirmation")}
            className="p-2 border border-gray-200 rounded-md"
          />
          {errors.passwordConfirmation && <p className="text-sm text-red-500">{errors.passwordConfirmation.message}</p>}
        </div>

        {trpcError && <p className="text-sm text-red-500 text-center">{trpcError}</p>}

        <PrimaryButton className="w-full" type="submit" disabled={!canSubmit}>
          パスワードを変更する
        </PrimaryButton>
      </form>
      {showSuccessMessage && (
        <Success message="パスワードを変更しました" onClose={() => setShowSuccessMessage(false)} />
      )}
    </div>
  );
};
